/**
 * Error dictionary (T11-06): every `error.code` of `shop_common.errors` (catalog.json, generated
 * from libs/py-shop-common) → plain staff language in ja/th/en + "what to do next". Screens show
 * `describeError(e).message`; system words (table/field names, HTTP, JSON…) never reach the UI.
 */
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { isApiErrorLike } from "@/lib/api/errors";

import catalog from "./catalog.json";

export interface ErrorDescription {
  message: string;
  hint: string | null;
  requestId: string | null;
  code: string;
  status: number;
}

export const CATALOG_CODES: readonly string[] = catalog.codes.map((c) => c.code);

function statusKey(status: number): string {
  if (status === 0) {
    return "network";
  }
  if (status >= 500) {
    return "5xx";
  }
  return String(status);
}

function hintStatusKey(status: number): string | null {
  if (status === 0) {
    return "network";
  }
  if (status >= 500) {
    return "5xx";
  }
  return ["401", "403", "409", "422", "429"].includes(String(status)) ? String(status) : null;
}

export function describeError(error: unknown, t: TFunction): ErrorDescription {
  if (!isApiErrorLike(error)) {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    return {
      message: t(offline ? "fallback.network" : "fallback.unknown", { ns: "apiErrors" }),
      hint: offline ? t("hints.status.network", { ns: "apiErrors" }) : null,
      requestId: null,
      code: offline ? "network_error" : "unknown",
      status: 0,
    };
  }
  const { code, status } = error;
  const requestId = typeof error.requestId === "string" ? error.requestId : null;
  const statusFallback = t(`fallback.${statusKey(status)}`, {
    ns: "apiErrors",
    defaultValue: t("fallback.unknown", { ns: "apiErrors" }),
  });
  const message =
    code === "network_error"
      ? t("fallback.network", { ns: "apiErrors" })
      : t(`codes.${code}`, { ns: "apiErrors", defaultValue: statusFallback });
  const specificHint = t(`hints.codes.${code}`, { ns: "apiErrors", defaultValue: "" });
  const statusHint = hintStatusKey(code === "network_error" ? 0 : status);
  const hint =
    specificHint ||
    (statusHint ? t(`hints.status.${statusHint}`, { ns: "apiErrors", defaultValue: "" }) : "") ||
    null;
  return { message, hint, requestId, code, status };
}

export function useDescribeError(): (error: unknown) => ErrorDescription {
  const { t } = useTranslation();
  return (error: unknown) => describeError(error, t);
}
